import { Headset } from "@assets";
import { LFlex } from "@components";
import { LProgressCircle } from "@components/LProgressCircle";
import { AssessmentTimeout } from "@fassessment/components/AssessmentTimeout";
import { AudioPlaybackAttemptDiagnostic, playAudioWithRetryAsync } from "@fassessment/hooks/useAudio";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AssessmentStatus, SttSourceType } from "@store/generalTypes";
import { flatten, isEqual, uniq, uniqWith, zip } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { getLocale, Translate, translate } from "react-i18nify";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LSubTitle } from "../../../common/components/LSubtitle";
import { LTitle } from "../../../common/components/LTitle";
import { sleepFor, useCountDown } from "../../../common/helpers/countHelper";
import {
    CategorizedWords,
    filterAndMatch,
    flattenAndCategorize,
    getUniqueWords,
    getWordsAndAlternatesFromListCategorized
} from "../../../common/helpers/instructionHelper";
import { NavItems } from "../../../common/helpers/navigation";
import { updateCurrentFlow } from "../../../common/store/assessmentFlowSlice";
import { Word, WordCategory } from "../../../common/store/assessmentSlice";
import { Env } from "../../../env";
import { useApi } from "../../../services/useApi";
import { Colors } from "../../../styles/colors";
import { ResultType } from "../../../utils/stt/useSpeechToText";
import { AssessmentShell } from "../components/AssessmentShell";
import { useCustomSpeechToText } from "../hooks/useCustomSpeechToText";
import { useScreenWakeLock } from "../hooks/useScreenWakeLock";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { v4 as uuidv4 } from "uuid";
import { trackDiagnosticException, trackDiagnosticTrace } from "@utils/diagnostics/appInsightsDiagnostics";

const prettyWordCategory = (word: WordCategory, locale?: string | undefined): string => {
    switch (word) {
        case WordCategory.Fruit:
            return translate("assessments.fruits", undefined, locale ? { locale } : undefined);
        case WordCategory.Clothes:
            return translate("assessments.clothing", undefined, locale ? { locale } : undefined);
        case WordCategory.Instrument:
            return translate("assessments.instruments", undefined, locale ? { locale } : undefined);
        case WordCategory.Vegetable:
            return translate("assessments.vegetables", undefined, locale ? { locale } : undefined);
        case WordCategory.Animal:
            return translate("assessments.animals", undefined, locale ? { locale } : undefined);
        case WordCategory.Profession:
            return translate("assessments.professions", undefined, locale ? { locale } : undefined);
        case WordCategory.School:
            return translate("assessments.school", undefined, locale ? { locale } : undefined);
        case WordCategory.Household:
            return translate("assessments.household", undefined, locale ? { locale } : undefined);
        case WordCategory.Beverage:
            return translate("assessments.beverage", undefined, locale ? { locale } : undefined);
    }
};

const LIST_A = 0;
const LIST_B = 1;
const CUED_RECALL_PROMPT_SETTLE_DELAY_SECONDS = 0.8;
const CUED_RECALL_AUDIO_FAILURE_FALLBACK_DELAY_SECONDS = 0.5;
const CUED_RECALL_AUDIO_RETRY_DELAY_MS = 350;

const getCategoryList = (category: WordCategory): WordCategory[] => {
    switch (category) {
        case WordCategory.Fruit:
        case WordCategory.Instrument:
        case WordCategory.Clothes:
            return [WordCategory.Fruit, WordCategory.Instrument, WordCategory.Clothes];
        case WordCategory.Vegetable:
        case WordCategory.Profession:
        case WordCategory.Animal:
            return [WordCategory.Vegetable, WordCategory.Profession, WordCategory.Animal];
        case WordCategory.Beverage:
        case WordCategory.Household:
        case WordCategory.School:
            return [WordCategory.Beverage, WordCategory.Household, WordCategory.School];
        default:
            return [WordCategory.Fruit, WordCategory.Instrument, WordCategory.Clothes];
    }
};

export const AssessmentCuedRecalls = (): JSX.Element => {
    const responsesByStepName = useRef<Record<string, ResultType[]>>({});
    const promptOperationRef = useRef<Promise<void>>();
    const uploadResultInFlightRef = useRef(false);
    const [currentCategory, setCurrentCategory] = useState(0);
    const [isAudioPaying, setIsAudioPaying] = useState(true);

    const assessment = useAppSelector((x) => x.assessment);
    const showTimer = useAppSelector((x) => x.globalSettings.showTimers);
    const sttDiagnosticsEnabled = useAppSelector((x) => x.globalSettings.enableSttDiagnostics);
    const wakeLockEnabled = useAppSelector((x) => x.globalSettings.enableAssessmentWakeLock);

    const appInsights = useAppInsightsContext();

    const listA = assessment.wordLists[LIST_A].words;
    const listB = assessment.wordLists[LIST_B].words;

    const [categories] = useState(getCategoryList(listA[0].category));

    const [assessmentFlow] = useState(useAppSelector((x) => x.assessmentFlow));
    const cuedRecallTime = useAppSelector((x) => x.globalSettings.cuedRecall);

    const currentFlow = assessmentFlow.flows[assessmentFlow.currentFlow];

    useScreenWakeLock({
        enabled: wakeLockEnabled,
        diagnosticsEnabled: sttDiagnosticsEnabled,
        area: "cued-recall",
        context: {
            flowName: currentFlow.name,
            currentCategory,
            category: categories[currentCategory]
        }
    });

    const currentListWords = getWordsAndAlternatesFromListCategorized(
        assessment.wordLists[currentFlow.list].words,
        categories[currentCategory]
    );

    const [init, setInit] = useState(true);
    const [categorizedWords, setCategorizedWords] = useState<CategorizedWords>({});

    const allTheWordsThatWereTranslation = `assessments.allTheWordsThatWere${currentFlow.name.replaceAll(" ", "")}`;

    const logData = {
        id: uuidv4(),
        assessmentId: `cued-${assessment.id}`,
        flow: "",
        part: 0,
        stepName: "",
        context: "",
        text: ""
    };

    useEffect(() => {
        logCuedFlow("start useEffect");

        dispatch(updateCurrentFlow());

        let newCategorizedWords: CategorizedWords = {};
        newCategorizedWords = flattenAndCategorize(newCategorizedWords, listA, currentFlow.list !== LIST_A);
        newCategorizedWords = flattenAndCategorize(newCategorizedWords, listB, currentFlow.list !== LIST_B);
        newCategorizedWords = flattenAndCategorize(newCategorizedWords, assessment.intrusions as Word[], true);

        logCuedFlow("trace useEffect", newCategorizedWords);

        setCategorizedWords(newCategorizedWords);

        logCuedFlow("end useEffect");
    }, []);

    useEffect(() => {
        logCuedFlow("start useEffect currentCategory", init);

        if (!init) {
            void handlePrompt();
        }

        logCuedFlow("end useEffect currentCategory", init);
    }, [currentCategory]);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { bulkAssessmentResponse, uploadAssessmentStepResponse, updateAssessmentStatus } = useApi();
    const { results, startSpeechToText, recordingVolume, stopSpeechToText, stream, destroy, hardResetAudioPipeline } =
        useCustomSpeechToText({
            context: currentListWords,
            fullAudio: true,
            useStopTalking: false
        });

    const getStreamDiagnostics = (): Record<string, string | number | boolean | undefined> => {
        const audioTracks = stream?.getAudioTracks() ?? [];

        return {
            streamActive: stream?.active,
            streamId: stream?.id,
            streamAudioTrackCount: audioTracks.length,
            streamAudioTrackStates: audioTracks.map((track) => `${track.readyState}:${track.enabled}`).join("|")
        };
    };

    const startCuedRecallRecording = async (
        stepName: string,
        textAudio: string,
        audioPlayed: boolean,
        promptAudioElapsed: number
    ): Promise<void> => {
        logCuedFlow("trace handlePrompt startSpeechToText");

        await startSpeechToText(undefined, currentListWords, {
            stepName,
            currentCategory,
            promptAudioPlayed: audioPlayed
        });

        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "cued-recall",
            event: audioPlayed ? "prompt:recording-started" : "prompt:audio-failed-recording-started",
            properties: {
                flowName: currentFlow.name,
                stepName,
                currentCategory,
                category: categories[currentCategory],
                contextWordCount: currentListWords.length,
                audioPlayed,
                audioUrl: textAudio,
                promptAudioElapsed,
                ...getStreamDiagnostics()
            }
        });

        logCuedFlow("trace handlePrompt startSpeechToText");

        resetCounter();
        setIsAudioPaying(false);
    };

    const handlePrompt = useCallback(async () => {
        if (promptOperationRef.current) {
            return await promptOperationRef.current;
        }

        const operation = handlePromptInternal();
        promptOperationRef.current = operation;

        try {
            await operation;
        } finally {
            if (promptOperationRef.current === operation) {
                promptOperationRef.current = undefined;
            }
        }
    }, [stream, currentCategory]);

    const handlePromptInternal = async (): Promise<void> => {
        logCuedFlow("start handlePrompt", categories[currentCategory]);
        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "cued-recall",
            event: "prompt:begin",
            properties: {
                flowName: currentFlow.name,
                currentCategory,
                category: categories[currentCategory],
                contextWordCount: currentListWords.length,
                ...getStreamDiagnostics()
            }
        });

        if (categories[currentCategory]) {
            setIsAudioPaying(true);
            const textAudio = `${
                Env.REACT_APP_BLOB_URL
            }/instructions-${getLocale()}-us/allTheWordsThatWere${currentFlow.name.replaceAll(" ", "")}-${
                categories[currentCategory]
            }.mp3`;
            const stepName = `${currentFlow.name} - ${prettyWordCategory(categories[currentCategory], "en")}`;

            logCuedFlow("trace handlePrompt playAudioAsync");
            await sleepFor(CUED_RECALL_PROMPT_SETTLE_DELAY_SECONDS);
            const promptAudioStart = Date.now();
            const audioAttempts = new Array<AudioPlaybackAttemptDiagnostic>();

            const audioPlayed = await playAudioWithRetryAsync(
                () => new Audio(textAudio),
                2,
                10000,
                (attempt) => {
                    audioAttempts.push(attempt);
                    trackDiagnosticTrace({
                        enabled: sttDiagnosticsEnabled,
                        area: "cued-recall",
                        event: "prompt:audio-attempt",
                        properties: {
                            flowName: currentFlow.name,
                            currentCategory,
                            category: categories[currentCategory],
                            audioUrl: textAudio,
                            ...attempt,
                            ...getStreamDiagnostics()
                        }
                    });
                },
                CUED_RECALL_AUDIO_RETRY_DELAY_MS
            );
            const promptAudioElapsed = Date.now() - promptAudioStart;

            if (!audioPlayed) {
                const lastAttempt = audioAttempts[audioAttempts.length - 1];

                trackDiagnosticTrace({
                    enabled: sttDiagnosticsEnabled,
                    area: "cued-recall",
                    event: "prompt:audio-failed",
                    properties: {
                        flowName: currentFlow.name,
                        currentCategory,
                        category: categories[currentCategory],
                        contextWordCount: currentListWords.length,
                        elapsed: promptAudioElapsed,
                        audioUrl: textAudio,
                        attemptCount: audioAttempts.length,
                        lastAttemptReason: lastAttempt?.reason,
                        lastAttemptElapsed: lastAttempt?.elapsed,
                        lastAttemptReadyState: lastAttempt?.readyState,
                        lastAttemptNetworkState: lastAttempt?.networkState,
                        lastAttemptAudioErrorCode: lastAttempt?.audioErrorCode,
                        lastAttemptAudioErrorMessage: lastAttempt?.audioErrorMessage,
                        attempts: audioAttempts,
                        ...getStreamDiagnostics()
                    }
                });

                const resetSucceeded = await hardResetAudioPipeline({
                    flowName: currentFlow.name,
                    currentCategory,
                    category: categories[currentCategory],
                    reason: "cued-recall-prompt-audio-failed",
                    preserveAudioContext: false,
                    audioUrl: textAudio
                });

                trackDiagnosticTrace({
                    enabled: sttDiagnosticsEnabled,
                    area: "cued-recall",
                    event: "prompt:audio-failed-hard-reset",
                    properties: {
                        flowName: currentFlow.name,
                        currentCategory,
                        category: categories[currentCategory],
                        resetSucceeded,
                        audioUrl: textAudio,
                        ...getStreamDiagnostics()
                    }
                });

                await sleepFor(CUED_RECALL_AUDIO_FAILURE_FALLBACK_DELAY_SECONDS);
                await startCuedRecallRecording(stepName, textAudio, false, promptAudioElapsed);
                logCuedFlow("end handlePrompt", categories[currentCategory]);
                return;
            }

            trackDiagnosticTrace({
                enabled: sttDiagnosticsEnabled,
                area: "cued-recall",
                event: "prompt:audio-complete",
                properties: {
                    flowName: currentFlow.name,
                    currentCategory,
                    category: categories[currentCategory],
                    contextWordCount: currentListWords.length,
                    elapsed: promptAudioElapsed,
                    audioUrl: textAudio,
                    attemptCount: audioAttempts.length,
                    attempts: audioAttempts,
                    ...getStreamDiagnostics()
                }
            });

            logCuedFlow("trace handlePrompt playAudioAsync");

            await startCuedRecallRecording(stepName, textAudio, true, promptAudioElapsed);
        }

        logCuedFlow("end handlePrompt", categories[currentCategory]);
    };

    const handleOnCounterDone = async (): Promise<void> => {
        logCuedFlow("start handleOnCounterDone");
        const stepName = `${currentFlow.name} - ${prettyWordCategory(categories[currentCategory], "en")}`;

        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "cued-recall",
            event: "timer:end",
            properties: {
                flowName: currentFlow.name,
                currentCategory,
                stepName,
                responseCount: responsesByStepName.current[stepName]?.length
            }
        });

        const shouldEnd = currentCategory === 2;

        // add other list words in the same category to improve intrusion occurrence
        const otherWords = new Array<string>();
        assessment.wordLists.forEach((x) => {
            const words = x.words.filter((w) => w.category === categories[currentCategory]).map((w) => w.value);
            otherWords.push(...words);
        });

        const contextListWord = uniq([...currentListWords, ...otherWords]);

        await stopSpeechToText(true, undefined, contextListWord, {
            stepName,
            shouldEnd,
            currentCategory
        });

        setCurrentCategory(currentCategory + 1);
        setIsAudioPaying(true);

        logCuedFlow("end handleOnCounterDone");
    };

    const [counter, resetCounter] = useCountDown({
        from: cuedRecallTime,
        onDone: handleOnCounterDone,
        startOnMount: false
    });

    const uploadResult = async (
        audio: Blob,
        shouldEnd: boolean,
        stepName: string,
        category: number,
        stepResponses: ResultType[]
    ): Promise<void> => {
        if (uploadResultInFlightRef.current) {
            logCuedFlow("skip uploadResult already in flight", { shouldEnd, stepName, category });
            return;
        }

        uploadResultInFlightRef.current = true;
        logCuedFlow("start uploadResult", { shouldEnd, stepName, category });

        const responsesWords = getUniqueWords(stepResponses);
        const uniqWords = uniq(flatten(responsesWords).map((x) => x.trim().toLocaleLowerCase())).filter(
            (x) => x !== ""
        );

        let filteredWords = filterAndMatch(uniqWords, categorizedWords, categories[category]);
        filteredWords = uniqWith(
            filteredWords.filter((x) => !x.response.includes(" ")),
            isEqual
        );

        try {
            logCuedFlow("trace uploadResult bulkAssessmentResponse", { id: assessment.id, stepName, filteredWords });

            await bulkAssessmentResponse(assessment.id, {
                stepName,
                responses: filteredWords
            });

            logCuedFlow("trace uploadResult bulkAssessmentResponse");
        } catch (error) {
            logCuedFlow("error uploadResult bulkAssessmentResponse", error);
            trackDiagnosticException({
                enabled: sttDiagnosticsEnabled,
                area: "cued-recall",
                event: "upload:bulk-response:error",
                error,
                properties: {
                    assessmentId: assessment.id,
                    stepName
                }
            });
            uploadResultInFlightRef.current = false;
            return;
        }

        logCuedFlow("trace uploadResult uploadAssessmentAudioStepResponse", { id: assessment.id, stepName });

        const success = await uploadAssessmentAudioStepResponse(assessment.id, stepName, audio);

        if (!success) {
            await uploadAssessmentAudioStepResponse(assessment.id, stepName, audio);
        }

        logCuedFlow("trace uploadResult uploadAssessmentAudioStepResponse");

        if (shouldEnd) {
            destroy();
            if (!assessmentFlow.flows[assessmentFlow.currentFlow + 1]) {
                await updateAssessmentStatus(
                    assessment?.locationId ?? 0,
                    assessment.id ?? 0,
                    AssessmentStatus.Complete
                );

                navigate(NavItems.Assessments.AssessmentClosingVideo.path);
                return;
            }

            navigate(assessmentFlow.flows[assessmentFlow.currentFlow + 1].path);
            return;
        }

        uploadResultInFlightRef.current = false;
        logCuedFlow("end uploadResult");
    };

    const uploadAssessmentAudioStepResponse = async (
        assessmentId: number,
        stepName: string,
        audio: Blob
    ): Promise<boolean> => {
        let result = true;

        try {
            logCuedFlow("trace uploadAssessmentAudioStepResponse", { id: assessmentId, stepName });

            await uploadAssessmentStepResponse(assessmentId, stepName, audio);

            logCuedFlow("trace uploadAssessmentAudioStepResponse");
        } catch (error) {
            logCuedFlow("error uploadAssessmentAudioStepResponse", error);
            trackDiagnosticException({
                enabled: sttDiagnosticsEnabled,
                area: "cued-recall",
                event: "upload:audio:error",
                error,
                properties: {
                    assessmentId,
                    stepName,
                    blobSize: audio.size,
                    blobType: audio.type
                }
            });

            result = false;
        }

        return result;
    };

    useEffect(() => {
        logCuedFlow("start useEffect results", results?.length);

        if (!results?.length) return;
        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "cued-recall",
            event: "results:received",
            properties: {
                flowName: currentFlow.name,
                resultCount: results.length,
                currentCategory,
                shouldEnd: results[0]?.context?.shouldEnd,
                stepName: results[0]?.context?.stepName,
                sources: results.map((x) => x.source).join("|"),
                blobSize: results[0]?.speechBlob?.size
            }
        });

        const googleUniqList = flatten(
            results
                .filter((x) => x.source === SttSourceType.Google)
                .map((x) => uniq(x.transcript.toLocaleLowerCase().split(" ")))
        );
        const azureUniqList = flatten(
            results
                .filter((x) => x.source === SttSourceType.Azure)
                .map((x) => uniq(x.transcript.toLocaleLowerCase().split(" ")))
        );
        const interleaved = zip(googleUniqList, azureUniqList);
        const mergedList = flatten(interleaved).filter((item) => item !== undefined);
        const joinResults = uniq(mergedList).join(" ").trim();

        const highestConfidenceResult = results[0];

        const res: ResultType = {
            transcript: joinResults,
            speechBlob: highestConfidenceResult.speechBlob,
            elapsed: highestConfidenceResult.elapsed,
            intendedWord: highestConfidenceResult.intendedWord,
            confidence: highestConfidenceResult.confidence,
            context: highestConfidenceResult.context
        };

        logCuedFlow("trace useEffect results transcript", res?.transcript);

        const stepName = highestConfidenceResult.context?.stepName;

        if (stepName) {
            responsesByStepName.current[stepName] = [...(responsesByStepName.current[stepName] ?? []), res];
        }

        logCuedFlow("trace useEffect results transcript");

        logCuedFlow("trace useEffect results highestConfidenceResult", highestConfidenceResult?.context?.stepName);

        if (stepName) {
            void saveCuedRecall(
                stepName,
                highestConfidenceResult.context.shouldEnd,
                highestConfidenceResult.context.currentCategory
            );
        }

        logCuedFlow("end useEffect results");
    }, [results]);

    useEffect(() => {
        logCuedFlow("start useEffect stream");

        if (stream) {
            logCuedFlow("trace useEffect stream");

            void handlePrompt();
            setInit(false);

            logCuedFlow("trace useEffect stream");
        }

        logCuedFlow("end useEffect stream");
    }, [stream]);

    const progress = Math.floor(((cuedRecallTime - counter) * 100) / cuedRecallTime);
    const time = new Date((cuedRecallTime - (cuedRecallTime - counter)) * 1000).toISOString().slice(14, 19);

    const saveCuedRecall = async (stepName: string, shouldEnd: boolean, category: number): Promise<void> => {
        logCuedFlow("start saveCuedRecall", { stepName, shouldEnd, category });

        logCuedFlow("trace saveCuedRecall");

        const stepResponses = responsesByStepName.current[stepName] ?? [];
        const blob = stepResponses.length > 0 ? stepResponses[0].speechBlob ?? new Blob() : new Blob();
        void uploadResult(blob, shouldEnd, stepName, category, stepResponses);

        logCuedFlow("trace saveCuedRecall");

        responsesByStepName.current[stepName] = [];

        logCuedFlow("end saveCuedRecall");
    };

    const logCuedFlow = (text: string, context: any = {}): void => {
        if (!sttDiagnosticsEnabled || !text.startsWith("error")) {
            return;
        }

        logData.part = logData.part + 1;
        logData.flow = `${currentFlow.name}-${currentCategory}`;
        logData.text = text;
        logData.context = JSON.stringify(context);

        appInsights.trackTrace({
            message: JSON.stringify(logData),
            severityLevel: SeverityLevel.Information
        });
    };

    return (
        <AssessmentShell
            micVolume={currentCategory < 3 ? recordingVolume : 0}
            micIsRecording={!isAudioPaying && currentCategory < 3}
        >
            <div className="assessment-stage is-flex is-flex-direction-column" style={{ paddingBottom: "6rem" }}>
                {currentCategory < 3 ? (
                    <>
                        {showTimer && (
                            <div className="assessment-timer" style={{ position: "absolute", right: "8%", top: "2%" }}>
                                <LProgressCircle
                                    progress={progress}
                                    text={time}
                                    labelColor={Colors.primaryBlue}
                                    trackColor={Colors.primaryBlue}
                                    indicatorColor={Colors.primaryBlue}
                                />
                                <div>{results[0]?.transcript}</div>
                            </div>
                        )}
                        <LFlex alignCenter justifyCenter>
                            <LSubTitle size="1" color="has-text-dark-blue">
                                <Translate value={allTheWordsThatWereTranslation} />
                            </LSubTitle>
                            <LTitle size="1" color="has-text-dark-blue">
                                {prettyWordCategory(categories[currentCategory])}.
                            </LTitle>
                            <LFlex className={isAudioPaying ? "is-hidden-custom" : ""} row alignCenter justifyCenter>
                                <Headset />
                                <p className="mx-4 has-text-dark-blue">
                                    <Translate value="assessments.listening" />
                                    ...
                                </p>
                            </LFlex>
                        </LFlex>
                    </>
                ) : (
                    <div>
                        <LSubTitle size="1" color="has-text-dark-blue">
                            <Translate value="assessments.pleaseWait" />
                        </LSubTitle>
                        <FontAwesomeIcon icon={faSpinner} size="6x" color={Colors.darkBlue} className="fa-spin" />
                    </div>
                )}
            </div>
            <AssessmentTimeout micVolume={recordingVolume} />
        </AssessmentShell>
    );
};
